import { useEffect, useState } from "react";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import { CatalogEntriesTable } from "../CatalogEntriesTable";
import {
  fetchSupplierCatalog,
  refreshSupplierCatalog,
  uploadSupplierCatalog,
  type SupplierCatalogEntry,
  type SupplierCatalogRefresh,
} from "../api";

// Catalogo dimensioni/colli di UN fornitore (smontaggio della vecchia
// CatalogoDimensioniSection multi-fornitore: ogni tab ora mostra solo il
// suo). Upload Excel + Re-parsa SOLO dove ``hasListino`` (Itamoby/Ityhome);
// gli altri sono sola consultazione (dati da script/giacenze/GSheet).

interface SupplierCatalogSectionProps {
  supplierId: string;
  hasListino: boolean;
  /** Bump per forzare il refetch (es. dopo import Bizzotto da GSheet). */
  reloadToken?: number;
}

export function SupplierCatalogSection({
  supplierId,
  hasListino,
  reloadToken = 0,
}: SupplierCatalogSectionProps) {
  // La tabella mostra al massimo 100 righe e per il resto indirizza alla
  // ricerca (comportamento storico). Scarichiamo quindi solo la prima pagina
  // da 100: il total server (filtrato) alimenta il conteggio e l'hint "e
  // altri N SKU". Niente "carica altri": si cerca per trovare, come prima.
  const PAGE_SIZE = 100;
  const [items, setItems] = useState<SupplierCatalogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState<"upload" | "refresh" | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<SupplierCatalogRefresh | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");

  // Debounce ricerca (300ms): la ricerca SKU e' server-side (catalogo fino a
  // ~3600 SKU non viene piu' scaricato tutto), quindi evitiamo un fetch per
  // ogni keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedFilter(filter), 300);
    return () => window.clearTimeout(t);
  }, [filter]);

  // Cambio fornitore/ricerca/reload → ricarica dalla prima pagina.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    void fetchSupplierCatalog(supplierId, {
      q: debouncedFilter || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    })
      .then((data) => {
        if (cancelled) return;
        // Boundary: non fidarsi della shape (una risposta malformata non deve
        // far crashare la tabella con items.length su undefined).
        setItems(data.items ?? []);
        setTotal(data.total ?? 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(prettifyApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [supplierId, reloadToken, debouncedFilter]);

  async function reloadEntries() {
    const data = await fetchSupplierCatalog(supplierId, {
      q: debouncedFilter || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    });
    setItems(data.items ?? []);
    setTotal(data.total ?? 0);
  }

  async function runRefresh() {
    setBusy("refresh");
    setError(null);
    setLastRefresh(null);
    try {
      const result = await refreshSupplierCatalog(supplierId);
      setLastRefresh(result);
      await reloadEntries();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(null);
    }
  }

  async function runUpload(file: File) {
    // Upload Excel + refresh automatico in un'unica call. Replace=true:
    // il backend rimuove i listini vecchi prima di salvare il nuovo,
    // garantendo sempre 1 listino attivo per fornitore.
    setBusy("upload");
    setError(null);
    setLastRefresh(null);
    try {
      const result = await uploadSupplierCatalog(supplierId, file, true);
      setLastRefresh(result);
      await reloadEntries();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(null);
    }
  }

  // Empty-hint per fornitore: il default della tabella suggerisce "Carica
  // Excel", fuorviante dove il bottone non c'è (Bizzotto → import GSheet;
  // Cosma/AllService → sorgenti esterne).
  const emptyHint = hasListino
    ? undefined
    : supplierId === "bizzotto"
      ? "Clicca «Importa catalogo da Google Sheet» qui sopra per popolare il catalogo."
      : "I dati di questo fornitore arrivano da una sorgente esterna (script/giacenze): niente upload da UI.";

  return (
    <Card className="mb-4 p-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display text-[16px] font-bold text-ink">
          Catalogo dimensioni e colli
          <span className="ml-2 tabular-nums font-normal text-[11px] text-ink-2 tabular-nums">
            {total} SKU
          </span>
        </h2>
        {hasListino ? (
          <div className="flex items-center gap-2">
            <label
              className={[
                "cursor-pointer rounded-md border border-accent bg-accent-2 px-2.5 py-1 text-[11.5px] font-medium text-white",
                busy !== null ? "opacity-50 pointer-events-none" : "hover:brightness-95",
              ].join(" ")}
              title="Carica un nuovo listino Excel: sostituisce il vecchio e popola il DB"
            >
              {busy === "upload" ? "Carico..." : "Carica Excel"}
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                disabled={busy !== null}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void runUpload(file);
                  // Reset input value cosi' lo stesso file puo' essere
                  // re-uploadato (il browser non scatena onChange se il
                  // file e' identico al precedente).
                  event.target.value = "";
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => void runRefresh()}
              disabled={busy !== null}
              title="Ri-parsa il file Excel gia' presente (per re-import dopo modifica manuale)"
              className="rounded-full border border-line bg-card px-2.5 py-1 text-[11.5px] text-ink-2 hover:border-accent disabled:opacity-50"
            >
              {busy === "refresh" ? "Aggiorno..." : "Re-parsa"}
            </button>
          </div>
        ) : (
          <span
            className="rounded-lg border border-line bg-paper-2 px-2.5 py-1 text-[10.5px] text-ink-2"
            title="Questo fornitore non ha un listino Excel caricabile dalla UI (dati aggiornati da altra sorgente)."
          >
            solo consultazione
          </span>
        )}
      </div>
      {supplierId !== "bizzotto" && (
        <p className="text-[12.5px] text-ink-2 leading-relaxed mb-3">
          Pesi e dimensioni colli/bancale di ogni SKU: <strong>indispensabili</strong> per generare
          automaticamente le LDV corrette a SDA/BRT/GLS senza digitare i valori a mano.
        </p>
      )}

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Cerca SKU..."
        className="mb-3 w-full max-w-xs rounded-2xl border border-line bg-card px-3 py-1.5 text-[12px] outline-none focus:border-accent"
      />

      <ErrorBanner message={error} className="mb-3" />

      <div className="rounded-lg border border-line bg-paper">
        <CatalogEntriesTable
          items={items}
          totalUnfiltered={total}
          emptyHint={emptyHint}
          loading={loading}
        />
      </div>

      {lastRefresh && (
        <div className="mt-3 rounded-full border border-line bg-paper-2 px-3 py-2 text-[11.5px] text-ink-2">
          Ultimo refresh: <b>{lastRefresh.label}</b>: {lastRefresh.files_seen} file ·{" "}
          {lastRefresh.skus_parsed} SKU · {lastRefresh.inserted} inseriti · {lastRefresh.updated}{" "}
          aggiornati · {lastRefresh.deleted} rimossi
          {lastRefresh.error && <span className="text-red"> · ERRORE: {lastRefresh.error}</span>}
        </div>
      )}
    </Card>
  );
}
